import { useCallback, useState } from 'react';

import type { ICampaignAttributes } from '@feathr/blackbox';
import { CampaignClass, EIntegrationTypes } from '@feathr/blackbox';
import { useFlags } from '@feathr/extender/state';

type TDeprecatedMetaCampaignClass = CampaignClass.Facebook | CampaignClass.EmailListFacebook;

export interface IMetaDeprecationState {
  /** The deprecated campaign type the user selected, or undefined if no deprecation flow is active. */
  deprecatedMetaType: TDeprecatedMetaCampaignClass | undefined;
  /** Whether the deprecation modal should be visible. */
  isModalOpen: boolean;
}

export interface IUseMetaDeprecationReturn {
  /**
   * Checks whether a campaign type should trigger the deprecation flow.
   * Call this in handleTypeChange before other step logic.
   *
   * @returns true if the type was intercepted (caller should return early).
   */
  interceptDeprecatedType: (campaignType: CampaignClass) => boolean;
  /** Applies Meta integration overrides to campaign attributes when creating from the deprecation flow. */
  applyIntegrationOverrides: (overrides: Partial<ICampaignAttributes>) => void;
  /** Confirms the deprecation modal — switches to Pixl Plus. Returns the new campaign type. */
  handleConfirm: () => CampaignClass.Omni;
  /** Cancels the deprecation modal — clears the deprecated type. */
  handleCancel: () => void;
  /** Resets the deprecation state. Call this from the parent reset function. */
  reset: () => void;
  /** Current deprecation state for rendering the modal. */
  state: IMetaDeprecationState;
}

/**
 * Encapsulates the Meta campaign deprecation flow.
 *
 * When `metaV2` is enabled, selecting Meta Retargeting or Meta Email Mapping
 * triggers a deprecation modal that redirects the user to create a Pixl Plus
 * campaign with the Meta channel pre-selected.
 */
function useMetaDeprecation(): IUseMetaDeprecationReturn {
  const { metaV2 } = useFlags();
  const [deprecatedMetaType, setDeprecatedMetaType] = useState<
    TDeprecatedMetaCampaignClass | undefined
  >(undefined);

  const interceptDeprecatedType = useCallback(
    (campaignType: CampaignClass): boolean => {
      if (
        metaV2 &&
        (campaignType === CampaignClass.Facebook ||
          campaignType === CampaignClass.EmailListFacebook)
      ) {
        setDeprecatedMetaType(campaignType);
        return true;
      }
      return false;
    },
    [metaV2],
  );

  const applyIntegrationOverrides = useCallback(
    (overrides: Partial<ICampaignAttributes>): void => {
      overrides.integrations = deprecatedMetaType ? [EIntegrationTypes.Meta] : [];
    },
    [deprecatedMetaType],
  );

  const handleConfirm = useCallback((): CampaignClass.Omni => {
    setDeprecatedMetaType(undefined);
    return CampaignClass.Omni;
  }, []);

  const handleCancel = useCallback((): void => {
    setDeprecatedMetaType(undefined);
  }, []);

  const reset = useCallback((): void => {
    setDeprecatedMetaType(undefined);
  }, []);

  const state: IMetaDeprecationState = {
    deprecatedMetaType,
    isModalOpen: deprecatedMetaType !== undefined,
  };

  return {
    interceptDeprecatedType,
    applyIntegrationOverrides,
    handleConfirm,
    handleCancel,
    reset,
    state,
  };
}

export default useMetaDeprecation;
